import React, { useEffect, useState } from "react";
import Switch from "react-switch";
import { useTranslation } from "react-i18next";
import {
  getMemberById,
  updateMembershipAutoPay,
} from "../../api/membershipAPI";
import { useDispatch, useSelector } from "react-redux";
import {
  selectMembershipDetails,
  setMembership,
} from "../../store/slices/membershipSlice";
import { useTheme } from "../../context/ThemeContext";
import { captureApiError } from "../../utils/sentry";
import { toast } from "react-toastify";
import { getPaymentErrorMessage } from "../../utils/paymentErrors";

const AutopaySwitch = ({ membershipId, token }: any) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation('membership');
  const [autopayEnabled, setAutopayEnabled] = useState(false);
  // The member's own election: also clear what they owe from earlier months,
  // so autopay actually leaves the account at $0. This is the only setting
  // that can charge them MORE than the invoice they were sent, which is why
  // it lives here rather than in the club's settings, and defaults to off.
  const [keepCurrent, setKeepCurrent] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const membership: any = useSelector(selectMembershipDetails);

  const toggleAutopay = async (checked: boolean) => {
    setLoading(true);
    try {
      const res = await updateMembershipAutoPay(token, membershipId, {
        autoPayment: checked,
      });
      setAutopayEnabled(res?.data?.autoPayment);
      // Switching autopay off retires the election with it (the backend does
      // the same) — leaving it set would silently re-arm arrears collection
      // if autopay were turned back on later.
      if (!checked) setKeepCurrent(false);
      fetchMembershipDetails();
    } catch (error) {
      captureApiError(error, "updateAutopay", { component: "AutopaySwitch" });
      // The switch used to stay put and say nothing. The server says why in
      // plain words (e.g. "Add a payment method and set it as the default
      // before turning on auto-pay").
      toast.error(
        getPaymentErrorMessage(error, "Automatic payments could not be changed. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleKeepCurrent = async (checked: boolean) => {
    setLoading(true);
    try {
      const res = await updateMembershipAutoPay(token, membershipId, {
        autoPayment: autopayEnabled,
        keepAccountCurrent: checked,
      });
      setKeepCurrent(res?.data?.keepAccountCurrent === true);
      fetchMembershipDetails();
    } catch (error) {
      captureApiError(error, "updateKeepAccountCurrent", { component: "AutopaySwitch" });
      toast.error(getPaymentErrorMessage(error, "That setting could not be changed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const fetchMembershipDetails = async () => {
    // Prefer the prop over Redux — the prop is the authoritative
    // membership we're toggling, and Redux can be stale or unhydrated
    // (e.g. user landed on /member/update-card directly via email link
    // before membershipSlice rehydrated). Sentry CLUB-CENTRAL-TENANT-3Q
    // was firing here when membership was null.
    const id = membershipId || membership?.id;
    if (!id) {
      // Nothing to fetch — Redux hasn't hydrated yet and no prop fallback.
      // Skip silently; the parent will provide membershipId once available.
      return;
    }

    try {
      const response = await getMemberById(token, id);

      // Assuming the membership details have a field `id`
      if (response && response.data) {
        dispatch(setMembership(response?.data));
      }
    } catch (err) {
      captureApiError(err, "fetchMembershipDetails", { component: "AutopaySwitch" });
    }
  };

  useEffect(() => {
    setAutopayEnabled(membership?.settings?.autoPayment);
    // Strictly true — an absent or malformed flag must read as opted OUT.
    setKeepCurrent(membership?.settings?.keepAccountCurrent === true);
  }, [membershipId, membership]);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
      <span
        className={`text-sm font-medium ${
          isDarkMode ? "text-gray-300" : "text-gray-900"
        }`}
      >
        {t('billing.enableAutopay')}
      </span>
      <Switch
        aria-label={t('billing.enableAutopay')}
        onChange={toggleAutopay}
        checked={autopayEnabled}
        disabled={loading}
        onColor={isDarkMode ? "#3b82f6" : "#2563eb"} // blue-500 for dark, blue-600 for light
        offColor={isDarkMode ? "#4b5563" : "#d1d5db"} // gray-600 for dark, gray-300 for light
        offHandleColor={isDarkMode ? "#9ca3af" : "#ffffff"} // gray-400 for dark, white for light
        onHandleColor="#ffffff" // white handle when on
        uncheckedIcon={false}
        checkedIcon={false}
        height={20}
        width={40}
        handleDiameter={16}
        activeBoxShadow={
          isDarkMode ? "0 0 2px 3px #60a5fa" : "0 0 2px 3px #93c5fd"
        } // blue glow
      />

      {/* Only meaningful while autopay is on, so it appears with it. */}
      {autopayEnabled && (
        <div className="flex items-start gap-2 sm:ml-4">
          <input
            type="checkbox"
            id="keep-account-current"
            className="mt-0.5 accent-[var(--cc-action)]"
            checked={keepCurrent}
            disabled={loading}
            onChange={(e) => toggleKeepCurrent(e.target.checked)}
          />
          <label
            htmlFor="keep-account-current"
            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
          >
            Keep my account current
            <span
              className={`block text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Also pay off anything still owed from earlier months, so my balance reaches $0.
              This can charge more than a single invoice.
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

export default AutopaySwitch;
